/**
 * Design System - Border Tokens
 *
 * Border radius, width, and style tokens from Figma design
 */

export const borders = {
  // Border Radius
  radius: {
    none: '0px',
    xs: '2px', // Extra small - rounded-xs
    sm: '4px', // Small - rounded-sm
    md: '6px', // Medium - rounded-md
    lg: '8px', // Large - rounded-lg
    xl: '12px', // Extra Large - rounded-xl
    '2xl': '16px', // 2X Large - rounded-2xl
    '3xl': '24px', // 3X Large
    full: '9999px', // Full circle - rounded-full
  },

  // Border Width
  width: {
    none: '0px',
    thin: '1px',
    medium: '2px',
    thick: '4px',
  },

  // Border Styles
  style: {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
    none: 'none',
  },
} as const;

// Component-specific border configurations
export const componentBorders = {
  // Input fields
  input: {
    radius: borders.radius.lg, // 8px
    width: borders.width.thin, // 1px
    style: borders.style.solid,
  },

  // Buttons
  button: {
    radius: borders.radius.sm, // 4px
    width: borders.width.thin, // 1px
    style: borders.style.solid,
  },

  // Cards
  card: {
    radius: borders.radius.xl, // 12px
    width: borders.width.thin, // 1px
    style: borders.style.solid,
  },

  // Badges/Tags
  badge: {
    radius: borders.radius.sm, // 4px
    width: borders.width.thin, // 1px
    style: borders.style.solid,
  },

  // Avatar/Profile
  avatar: {
    radius: borders.radius.full, // Full circle
    width: borders.width.none, // No border
    style: borders.style.solid,
  },

  // Modal/Dialog
  modal: {
    radius: borders.radius['2xl'], // 16px
    width: borders.width.none, // No border
    style: borders.style.solid,
  },
} as const;

export type BorderToken = typeof borders;
export type ComponentBorderToken = typeof componentBorders;

// Helper to create border string
export const createBorder = (
  width: keyof typeof borders.width,
  style: keyof typeof borders.style,
  color: string
): string => {
  return `${borders.width[width]} ${borders.style[style]} ${color}`;
};
